import config from 'config/appConfig';
import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useEffect, useRef, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useFieldArray, useForm } from 'react-hook-form';
import type { Path, UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AcctState } from 'stores/atoms/acctState';
import type { FormResponseType } from 'types/api/formResponseType';
import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import {
  applyGeneratedImages,
  buildImageGeneratorItems,
  type ImageGeneratorItem,
} from 'pages/ws/formEdit/utils/formImageGeneration';
import {
  buildEditFormFromResponse,
  buildInitialFormForm,
  convertFormValuesToParam,
} from 'pages/ws/formEdit/utils/formFormConverter';
import { submit } from 'utils/app';
import { openPreviewUrl } from 'utils/form';
import { getProductionUrl } from 'utils/terms';
import {
  collectRequiredErrors,
  validateQuestionOptionCount,
  validateRespondentMailDivRequired,
} from 'pages/ws/formEdit/utils/formValidation';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

export interface PromptSample {
  title: string;
  prompt: string;
  withReferenceImage: boolean;
}

/**
 * useWsFormEdit の戻り値型。
 */
export interface UseWsFormEditResult {
  // form
  useFormMethods: UseFormReturn<FormEditFormStateType>;
  questionFieldArray: UseFieldArrayReturn<FormEditFormStateType, 'questions', 'id'>;
  resultFieldArray: UseFieldArrayReturn<FormEditFormStateType, 'results', 'id'>;
  respondentFieldArray: UseFieldArrayReturn<FormEditFormStateType, 'respondentFields', 'id'>;

  // status flags
  initialized: boolean;
  processingOnSave: boolean;
  processingOnPreview: boolean;
  processingOnPublish: boolean;
  processingOnPublishTerminate: boolean;
  processingOnReserveCancel: boolean;
  processingOnDelete: boolean;
  processingOnCreateQuestion: boolean;

  // domain state
  formState: FormResponseType | null;
  isExpiredPlan: boolean;
  canPublished: boolean;
  isUnpublished: () => boolean;
  isReadOnly: () => boolean;

  // modal state
  modalPublishOpenState: boolean;
  setModalPublishOpenState: (open: boolean) => void;
  modalConfirmOpenState: boolean;
  setModalConfirmOpenState: (open: boolean) => void;
  modalImageGeneratorOpenState: boolean;
  setModalImageGeneratorOpenState: (open: boolean) => void;
  modalConfirmTitle: string;

  // handlers
  onSave: () => Promise<void>;
  onPreview: () => Promise<void>;
  onPublishModalOpen: () => Promise<void>;
  onConfirmModalSubmit: () => void;
  onPublishModalSubmit: (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => Promise<void>;
  onPublishTerminate: () => Promise<void>;
  onReserveCancel: () => Promise<void>;
  onDelete: () => Promise<void>;
  onOpenImageGenerator: () => void;
  onImageGenerationComplete: (generatedImages: { key: string; imageKey: string }[]) => void;
  onCreateFormRef: React.MutableRefObject<(() => void) | undefined>;

  // image generator
  imageGeneratorPromptSamples: PromptSample[];
  imageGeneratorItems: ImageGeneratorItem[];

  // shared
  navigate: ReturnType<typeof useNavigate>;
  confirm: ReturnType<typeof useAppState>['confirm'];
}

// ------------------------------------------------------------------ //
// hook
// ------------------------------------------------------------------ //

/**
 * フォーム型コンテンツ作成・編集画面のカスタムフック。
 * すべての状態・副作用・APIコール・フォーム初期化を集約する。
 */
export function useWsFormEdit(): UseWsFormEditResult {
  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { formCd } = useParams<{ formCd: string }>();

  const [acctState] = useAtom(AcctState);

  const { updateAcctState, noticeMessageSuccess, noticeMessageFailure, confirm, canPublish } =
    useAppState();
  const {
    postFormRef,
    postFormPublish,
    postFormPublishTerminate,
    postFormDelete,
    postFormUpdate,
    postFormRegister,
  } = useApiState();

  // ローディングの制御設定
  const [initialized, setInitialized] = useState(false);
  const [processingOnSave, setProcessingOnSave] = useState(false);
  const [processingOnPreview, setProcessingOnPreview] = useState(false);
  const [processingOnPublish, setProcessingOnPublish] = useState(false);
  const [processingOnPublishTerminate, setProcessingOnPublishTerminate] = useState(false);
  const [processingOnReserveCancel, setProcessingOnReserveCancel] = useState(false);
  const [processingOnDelete, setProcessingOnDelete] = useState(false);
  const [processingOnCreateQuestion] = useState(false);

  // フォーム情報
  const [formState, setFormState] = useState<FormResponseType | null>(null);
  const [modalPublishOpenState, setModalPublishOpenState] = useState<boolean>(false);
  const [modalConfirmOpenState, setModalConfirmOpenState] = useState<boolean>(false);
  const [modalConfirmTitle, setModalConfirmTitle] = useState<string>('');
  const [modalImageGeneratorOpenState, setModalImageGeneratorOpenState] = useState<boolean>(false);

  // フォーム設定
  const useFormMethods = useForm<FormEditFormStateType>();
  const { control, handleSubmit, setValue, getValues, setError } = useFormMethods;
  const questionFieldArray = useFieldArray({
    control,
    name: 'questions',
  });
  const resultFieldArray = useFieldArray({
    control,
    name: 'results',
  });
  const respondentFieldArray = useFieldArray({
    control,
    name: 'respondentFields',
  });

  // バリデーションメッセージ
  const validAlertMessage = '入力に誤りがあります。赤文字で表示されている箇所を確認してください。';
  // プラン（利用期間切れ）
  const isExpiredPlan = acctState.company.plan.isExpired;
  // 公開可能
  const canPublished = canPublish(DivConst.items.contentsType.form.value);

  // チャットボットコンポーネントからonCreateForm関数を呼び出すためのref
  const onCreateFormRef = useRef<(() => void) | undefined>(undefined);

  /* ----------------------------------------------------------------------- */
  /* 画像一括生成 promptSamples（hook 内に保持）*/
  /* ----------------------------------------------------------------------- */
  const imageGeneratorPromptSamples: PromptSample[] = [
    {
      title: '参考画像にテイストを合わせる',
      prompt: `スタイル条件:
- 添付画像とテイストや配色を合わせ、並べても違和感のないデザインにしてください。ただし、参照してよいのは「配色/明度コントラスト」「質感/テクスチャ傾向」「線の太さ/密度」「陰影やノイズの粒度」「画全体のトーン（例: モノクロか低彩度か）」など視覚的スタイル特性のみです。
`,
      withReferenceImage: true,
    },
    {
      title: '暖かく光に満ちた、ロマンチックウェディングスタイル',
      prompt: `スタイル条件:
- 配色/明度コントラスト: クリーム、ベージュ、ソフトブラウンを基調とした、暖色系の落ち着いたカラーパレット。全体的に明るく、コントラストは低めで柔らかい印象にする。逆光やゴールデンアワーのような、暖かく輝くハイライトを取り入れる。
- 質感/テクスチャ傾向: 背景は美しく滑らかなボケ（bokeh）を表現し、被写体を際立たせる。肌や布などの質感は、シャープすぎずソフトでナチュラルに描写する。
- 線の太さ/密度: 輪郭はソフトフォーカス気味で、夢のような優しく穏やかな雰囲気を出す。
- 陰影やノイズの粒度: 陰影は柔らかく、自然光によって作られる滑らかなグラデーションを表現する。デジタルノイズのない、クリーンで高画質な仕上がりにする。
- 画全体のトーン: 彩度を抑えた、わずかにセピア調の温かみのあるトーンで統一する。ロマンチックで、エレガント、そして幸福感に満ちた雰囲気を目指す。`,
      withReferenceImage: false,
    },
    {
      title: '研修申込用',
      prompt: '学習と成長をイメージできる知的で洗練されたデザイン',
      withReferenceImage: false,
    },
    {
      title: '意見収集用',
      prompt: 'コミュニケーションと協力を表現する親しみやすいイメージ',
      withReferenceImage: false,
    },
  ];

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // eslint-disable-next-line react-hooks/immutability -- 初回マウント時にhoisting相当で_initStateを呼ぶ設計
        await _initState();
      } catch (e) {
        showBoundary(e);
      } finally {
        setInitialized(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時 1 回のみ実行する初期化処理
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 保存ボタン押下時の処理
   */
  const onSave = async () =>
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(
        async () => {
          try {
            // 選択肢数のバリデーション
            if (!_validQuestionOptions()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            // 回答者情報の取得項目の整合性検証
            const respondentMailDivError = validateRespondentMailDivRequired(getValues());
            if (respondentMailDivError) {
              setError(respondentMailDivError.name as Path<FormEditFormStateType>, {
                type: 'custom',
                message: respondentMailDivError.message,
              });
              noticeMessageFailure(validAlertMessage);
              return;
            }

            const response = await _save(
              formState?.formStatusDiv ?? DivConst.items.formStatus.unpublished.value
            );
            setFormState(response);

            // 処理完了メッセージの表示
            noticeMessageSuccess('フォーム型コンテンツの保存が完了しました。');
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validQuestionOptions();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * プレビューボタン押下時の処理
   */
  const onPreview = async () =>
    submit(
      processingOnPreview,
      setProcessingOnPreview,
      handleSubmit(
        async () => {
          try {
            let form = formState;

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }

            // 未稼働の場合は保存処理を実施
            if (_isUnpublished()) {
              // フォーム情報の更新
              form = await _save(DivConst.items.formStatus.unpublished.value);
              // フォーム型コンテンツ公開APIの呼び出し
              form = await postFormPublish({
                formId: form.id,
                mode: 'preview',
              });
              setFormState(form);

              // 保存後に自動でプレビュー画面を開くとポップアップブロック判定されるため、ユーザーにアクションを促す
              confirm({
                title: 'フォーム型コンテンツの保存が完了しました',
                message: `このままプレビュー画面を開きますか？`,
                onSubmit: async () => {
                  openPreviewUrl(form.previewCd);
                },
              });
            } else {
              // プレビュー画面を開く
              openPreviewUrl(form.previewCd);
            }
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 公開ボタン押下時の処理
   */
  const onPublishModalOpen = async () =>
    submit(
      processingOnPublish,
      setProcessingOnPublish,
      handleSubmit(
        async () => {
          try {
            // 未設定項目確認モーダルを開く
            if (!getValues().privacyPolicyUrl) {
              setModalConfirmTitle('プライバシーポリシーが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().termsUrl) {
              setModalConfirmTitle('利用規約が未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().copyright) {
              setModalConfirmTitle('コピーライトが未登録です');
              setModalConfirmOpenState(true);
              return;
            }
            if (!getValues().reportMailReplyAddress) {
              setModalConfirmTitle('返信元メールアドレスが未登録です');
              setModalConfirmOpenState(true);
              return;
            }

            // 必須検証の追加実施
            if (!_validRequiredControllers()) {
              noticeMessageFailure(validAlertMessage);
              return;
            }
            // フォーム型コンテンツ公開設定モーダルを開く
            setModalPublishOpenState(true);
          } catch (e) {
            showBoundary(e);
          }
        },
        // バリデーションエラーがある場合の処理
        () => {
          _validRequiredControllers();
          noticeMessageFailure(validAlertMessage);
        }
      )
    );

  /**
   * 未設定項目確認モーダルのOKボタンがクリックされた時の処理
   */
  const onConfirmModalSubmit = () => {
    setModalConfirmOpenState(false);
    submit(
      processingOnSave,
      setProcessingOnSave,
      handleSubmit(async () => {
        try {
          // 回答者情報の取得項目の整合性検証
          const respondentMailDivError = validateRespondentMailDivRequired(getValues());
          if (respondentMailDivError) {
            setError(respondentMailDivError.name as Path<FormEditFormStateType>, {
              type: 'custom',
              message: respondentMailDivError.message,
            });
            noticeMessageFailure(validAlertMessage);
            return;
          }

          const response = await _save(
            formState?.formStatusDiv ?? DivConst.items.formStatus.unpublished.value
          );
          setFormState(response);
          navigate(`/setting/${acctState.company.cd}/company`);
        } catch (e) {
          showBoundary(e);
        }
      })
    );
  };

  /**
   * 公開設定完了時の処理。
   */
  const onPublishModalSubmit = async (attr: {
    publishStartAt: number | null;
    publishEndAt: number | null;
    noIndexFlag: boolean;
  }) => {
    try {
      // ローディングの表示
      setProcessingOnPublish(true);
      // 固定項目
      setValue('publishStartAt', attr.publishStartAt as FormEditFormStateType['publishStartAt']);
      setValue('publishEndAt', attr.publishEndAt as FormEditFormStateType['publishEndAt']);
      setValue('noIndexFlag', attr.noIndexFlag);

      // フォーム情報の更新
      let response = await _save(DivConst.items.formStatus.reserved.value);

      // 即時公開の場合の処理
      if (attr.publishStartAt == null) {
        // フォーム型コンテンツ公開APIの呼び出し
        response = await postFormPublish({
          formId: response.id,
          mode: 'production',
        });
        updateAcctState(['company', 'activeContent', 'usage'], acctState.company.cd);
      } else {
        // 予約公開の場合は最新のフォーム情報を再取得（popupImageKey など含めるため）
        response = await postFormRef({
          formCd: response.cd,
        });
      }

      setFormState(response);
      // ローディングの停止
      setProcessingOnPublish(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 公開停止ボタン押下時の処理
   */
  const onPublishTerminate = async () => {
    try {
      // ローディングの表示
      setProcessingOnPublishTerminate(true);
      // フォーム型コンテンツ公開停止APIの呼び出し
      setFormState(
        await postFormPublishTerminate({
          formId: formState!.id,
          mode: 'production',
        })
      );
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnPublishTerminate(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 予約取消ボタン押下時の処理
   */
  const onReserveCancel = async () => {
    try {
      // ローディングの表示
      setProcessingOnReserveCancel(true);
      // フォーム型コンテンツ情報の更新
      const response = await _save(DivConst.items.formStatus.unpublished.value);
      setFormState(response);
      // ローディングの停止
      setProcessingOnReserveCancel(false);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 削除ボタン押下時の処理
   */
  const onDelete = async () => {
    try {
      // ローディングの表示
      setProcessingOnDelete(true);
      // フォーム情報の削除
      await postFormDelete({ formId: formState!.id });
      // 画面の状態を更新
      updateAcctState(['activeContent', 'usage'], acctState.company.cd);
      // ローディングの停止
      setProcessingOnDelete(false);
      // コンテンツ画面に遷移
      navigate(`/ws/${acctState.company.cd}/content/search`);
    } catch (e) {
      showBoundary(e);
    }
  };

  /**
   * 画像一括生成モーダルを開きます。
   */
  const onOpenImageGenerator = () => {
    setModalImageGeneratorOpenState(true);
  };

  /**
   * 画像一括生成完了時のコールバック関数
   */
  const onImageGenerationComplete = (generatedImages: { key: string; imageKey: string }[]) => {
    const values = getValues();
    const applied = applyGeneratedImages(values, generatedImages);

    setValue('imageKey', applied.imageKey ?? undefined);
    setValue('questions', applied.questions as unknown as FormEditFormStateType['questions']);
    setValue('results', applied.results as unknown as FormEditFormStateType['results']);
    setModalImageGeneratorOpenState(false);
  };

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 画面の状態を初期化します。
   */
  const _initState = async () => {
    // reportMailSenderAddress は config に依存するため utils に含められない。hook 側で明示的にセットする。
    setValue('reportMailSenderAddress', config.ses.from.address);

    // 新規登録の場合
    if (!formCd) {
      const initialValues = buildInitialFormForm(acctState);
      // privacyPolicyUrl / termsUrl / copyright は acctState 由来の固定フィールドとして別途セット
      setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl ?? '');
      setValue('reportMailReplyAddress', acctState.company.reportMailReplyAddress ?? '');
      setValue('copyright', acctState.company.copyright ?? '');
      setValue(
        'termsUrl',
        acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
          ? (acctState.company.termsUrl ?? '')
          : getProductionUrl(acctState.company.cd)
      );

      (Object.entries(initialValues) as [keyof FormEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as FormEditFormStateType[typeof key]);
        }
      );

      onCreateFormRef.current?.();
    }
    // 更新の場合
    else {
      const response = await postFormRef({ formCd });
      setFormState(response);

      // buildEditFormFromResponse が含まない acctState / config 由来の固定フィールドをセット
      setValue('privacyPolicyUrl', acctState.company.privacyPolicyUrl ?? '');
      setValue('reportMailReplyAddress', acctState.company.reportMailReplyAddress ?? '');
      setValue('copyright', acctState.company.copyright ?? '');
      setValue(
        'termsUrl',
        acctState.company.termsInputDiv === DivConst.items.termsInputType.url.value
          ? (acctState.company.termsUrl ?? '')
          : getProductionUrl(acctState.company.cd)
      );

      const editValues = buildEditFormFromResponse(response);
      (Object.entries(editValues) as [keyof FormEditFormStateType, unknown][]).forEach(
        ([key, value]) => {
          setValue(key, value as FormEditFormStateType[typeof key]);
        }
      );
    }
  };

  /**
   * フォーム情報を保存します。
   */
  const _save = async (formStatusDiv: number): Promise<FormResponseType> => {
    // eslint-disable-next-line no-useless-assignment -- 後続の条件分岐で上書きされるため初期値としてnullを設定
    let response: FormResponseType | null = null;
    const commonParam = {
      ...convertFormValuesToParam(getValues()),
      popupImageKey: getValues().popupImageKey,
    };
    // 新規登録の場合
    if (!formState) {
      response = await postFormRegister({
        companyId: acctState.company.id,
        formStatusDiv,
        ...commonParam,
      });
    }
    // 更新の場合
    else {
      response = await postFormUpdate({
        formId: formState.id,
        formStatusDiv,
        respondentFieldGroupId: formState.respondentFieldGroupId,
        ...commonParam,
      } as unknown as Parameters<typeof postFormUpdate>[0]);
    }
    // 画面の状態を更新
    updateAcctState(['company', 'activeContent'], acctState.company.cd);

    return response!;
  };

  /**
   * 設問の選択肢数を検証します。
   */
  const _validQuestionOptions = (): boolean => {
    const values = getValues();
    let isValid = true;

    values.questions.forEach((question, questionIndex) => {
      const errors = validateQuestionOptionCount(question, questionIndex);
      errors.forEach(error => {
        setError(error.name as Path<FormEditFormStateType>, {
          type: 'custom',
          message: error.message,
        });
        isValid = false;
      });
    });

    return isValid;
  };

  /**
   * 公開時に必須となる項目を検証します。
   */
  const _validRequiredControllers = (): boolean => {
    const values = getValues();
    const errors = collectRequiredErrors(values);
    errors.forEach(error => {
      setError(error.name as Path<FormEditFormStateType>, {
        type: 'custom',
        message: error.message,
      });
    });
    return errors.length === 0;
  };

  /**
   * 未稼働かどうかを判定します。
   */
  const _isUnpublished = (): boolean => {
    return (
      formState == null || formState.formStatusDiv === DivConst.items.formStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 公開関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * 未稼働かどうかを判定します（公開向け）。
   */
  const isUnpublished = (): boolean => _isUnpublished();

  /**
   * 読み取り専用かどうかを判定します。
   */
  const isReadOnly = (): boolean => {
    return (
      formState != null && formState.formStatusDiv !== DivConst.items.formStatus.unpublished.value
    );
  };

  /* ----------------------------------------------------------------------- */
  /* 戻り値 */
  /* ----------------------------------------------------------------------- */
  return {
    useFormMethods,
    questionFieldArray,
    resultFieldArray,
    respondentFieldArray,
    initialized,
    processingOnSave,
    processingOnPreview,
    processingOnPublish,
    processingOnPublishTerminate,
    processingOnReserveCancel,
    processingOnDelete,
    processingOnCreateQuestion,
    formState,
    isExpiredPlan,
    canPublished,
    isUnpublished,
    isReadOnly,
    modalPublishOpenState,
    setModalPublishOpenState,
    modalConfirmOpenState,
    setModalConfirmOpenState,
    modalImageGeneratorOpenState,
    setModalImageGeneratorOpenState,
    modalConfirmTitle,
    onSave,
    onPreview,
    onPublishModalOpen,
    onConfirmModalSubmit,
    onPublishModalSubmit,
    onPublishTerminate,
    onReserveCancel,
    onDelete,
    onOpenImageGenerator,
    onImageGenerationComplete,
    onCreateFormRef,
    imageGeneratorPromptSamples,
    get imageGeneratorItems() {
      const v = getValues();
      return buildImageGeneratorItems({
        ...v,
        questions: v.questions ?? [],
        results: v.results ?? [],
        respondentFields: v.respondentFields ?? [],
      });
    },
    navigate,
    confirm,
  };
}
